import React, { ReactEventHandler, useCallback, useEffect, useState } from 'react';
import { UIPopoverMenu, UIPopoverMenuProps } from '@/comps/ui/menu/UIPopoverMenu';
import { EllipsisVertical } from 'lucide-react';
import { UIPopoverMenuItem } from '@/comps/ui/menu/UIPopoverMenuItem';
import { IconType } from '@/constants/icons';
import { WithClassName } from '@/types/component';
import { cn } from '@/utils/shad';
import { LoadingSkeleton } from '@/comps/loading/LoadingSkeleton';
import { Separator } from '@/comps/primitives/separator';
import { Button } from '@/comps/primitives/button';
import { VariantProps } from 'class-variance-authority';
import { useTourContext } from '@/domains/tour/TourContext';

export type UIPopoverMenuItemType = {
  label?: string;
  icon?: IconType;
  onClick?: ReactEventHandler<HTMLButtonElement>;
  show?: boolean;
  /** Renders a divider above this item when it and the previous visible item are both shown. */
  separatorBefore?: boolean;
  className?: string;
  renderItem?: (
    item: UIPopoverMenuItemType,
    index: number,
    setOpen?: React.Dispatch<React.SetStateAction<boolean>>,
  ) => React.ReactNode;
};

type Props = WithClassName &
  Pick<UIPopoverMenuProps, 'align' | 'side'> & {
    items: UIPopoverMenuItemType[];
    iconProps?: WithClassName & { size?: number };
    onOpenChange?: (open: boolean) => void;
    isLoading?: boolean;
    variant?: VariantProps<typeof Button>['variant'];
    dataTestId?: string;
  };

export function ThreeDotsMenu({
  items,
  iconProps,
  className,
  align,
  side,
  onOpenChange,
  isLoading,
  variant,
  dataTestId,
}: Props) {
  const [open, setOpen] = useState(false);
  const { isActive: isTourActive } = useTourContext();

  // While the product tour highlights a menu, Radix would auto-close on outside focus/pointer events
  // when the driver.js overlay mounts. Ignore those close requests so the highlighted menu stays
  // open. The trigger still toggles via setOpen, and nebuly:tour-close-sidebar-menus force-closes.
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next && isTourActive) return;
      setOpen(next);
    },
    [isTourActive],
  );

  useEffect(() => {
    if (!isTourActive) return;
    const close = () => setOpen(false);
    document.addEventListener('nebuly:tour-close-sidebar-menus', close);
    return () => document.removeEventListener('nebuly:tour-close-sidebar-menus', close);
  }, [isTourActive]);

  // Keep parent in sync for every close path (outside click, Escape, item setOpen(false)),
  // not only the trigger click.
  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  return (
    <UIPopoverMenu
      className={className}
      open={open}
      onOpenChange={handleOpenChange}
      align={align}
      side={side}
      trigger={
        <Button
          variant={variant ?? 'ghost'}
          size="icon"
          className={cn(
            'h-4 w-4 text-muted-foreground hover:bg-inherit hover:text-accent-foreground hover:text-white',
            iconProps?.className,
          )}
          data-testid={dataTestId}
          onClick={(e) => {
            setOpen((prev) => !prev);
            e.stopPropagation();
          }}
          PrependIcon={EllipsisVertical}
          iconSize={iconProps?.size ?? 18}
        />
      }
    >
      <div className={cn('flex flex-col gap-0 p-1', items.length > 1 ? 'gap-1' : '')}>
        {isLoading ? (
          <>
            <LoadingSkeleton width={9} height={2} />
            <LoadingSkeleton width={9} height={2} />
            <LoadingSkeleton width={9} height={2} />
          </>
        ) : (
          items.map((item, index) => {
            const { label, icon, onClick, show, renderItem, separatorBefore } = item;
            if (show === false) return null;

            const previousVisibleItem = items.slice(0, index).some((entry) => entry.show !== false);
            const showSeparator = separatorBefore && previousVisibleItem;

            const content = renderItem ? (
              renderItem(item, index, setOpen)
            ) : (
              <UIPopoverMenuItem
                icon={icon}
                className={item.className}
                onClick={(e) => {
                  onClick?.(e);
                }}
              >
                {label}
              </UIPopoverMenuItem>
            );

            return (
              <React.Fragment key={index}>
                {showSeparator ? <Separator className="-mx-1 my-1 w-[calc(100%+0.5rem)] bg-muted" /> : null}
                {content}
              </React.Fragment>
            );
          })
        )}
      </div>
    </UIPopoverMenu>
  );
}
